import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import config from '../../Config';
import Swal from 'sweetalert2';

function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    const email = e.target.email.value;
    const password = e.target.password.value;

    setLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('token', data.userData.token);
        localStorage.setItem('role', data.userData.role);
        localStorage.setItem('userId', data.userData.userId);

        Swal.fire({
          icon: 'success',
          title: 'Login Successful!',
          text: 'Redirecting to dashboard...',
          timer: 1500,
          showConfirmButton: false,
        });

        setTimeout(() => navigate('/dashboard'), 1500);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Login Failed',
          text: data.message || 'Invalid credentials',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Something Went Wrong',
        text: 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShowForgot = () => {
    setForgotEmail('');
    setForgotError('');
    setView('forgot');
  };

  const handleBackToLogin = () => {
    if (forgotLoading) return;
    setView('login');
  };

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleForgotPasswordSubmit = async () => {
    if (!forgotEmail.trim()) {
      setForgotError('Email is required');
      return;
    }
    if (!isValidEmail(forgotEmail.trim())) {
      setForgotError('Please enter a valid email address');
      return;
    }

    setForgotLoading(true);
    try {
      const response = await fetch(`${config.apiBaseUrl}/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });

      const data = await response.json();

      if (response.ok && (data.success ?? true)) {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: data.message || 'Password reset instructions have been sent to your email.',
          timer: 1800,
          showConfirmButton: false,
        });
        setForgotEmail('');
        setView('login');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Request Failed',
          text: data.message || 'Unable to process your request.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Something Went Wrong',
        text: 'Please try again.',
      });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        background: 'linear-gradient(to right, #6a11cb, #2575fc)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        overflow: 'hidden',
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: '#fff',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              {view === 'login' ? 'Admin Panel Login' : 'Reset Password'}
            </Typography>
            {view === 'forgot' && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, textAlign: 'center' }}
              >
                Enter your registered email and we'll send you instructions to reset your password.
              </Typography>
            )}
          </Box>

          {view === 'login' ? (
            <Box component="form" onSubmit={handleLogin} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                disabled={loading}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                disabled={loading}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={handleShowForgot}
                  sx={{ fontWeight: 500 }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 2,
                  mb: 2,
                  p: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleForgotPasswordSubmit();
              }}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                autoFocus
                label="Email Address"
                type="email"
                value={forgotEmail}
                disabled={forgotLoading}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  if (forgotError) setForgotError('');
                }}
                error={Boolean(forgotError)}
                helperText={forgotError || ' '}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={forgotLoading}
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                }}
              >
                {forgotLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Instructions'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={handleBackToLogin}
                  sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ArrowBackIcon fontSize="inherit" /> Back to Login
                </Link>
              </Box>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Powered by{' '}
              <Link
                href="https://www.ytctechventures.com/"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                ytctechventures
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
